import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Components
import CustomVueListContainer, { CustomVueList } from '../CustomVueList/CustomVueList';
import {
  CustomVueSectionTitle,
  CustomVueSectionTitleActions,
  CustomVueSectionContent,
  CustomVueSectionFooter,
} from '../../atoms/CustomVueSection/CustomVueSection';
import SearchField from '../../molecules/SearchField/SearchField';
import SearchGroupList from '../../molecules/SearchGroupList/SearchGroupList';
import SearchListItem, { SearchListItemActions } from '../../molecules/SearchListItem/SearchListItem';
import IconButton from '../../atoms/IconButton/IconButton';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import Button from '../../atoms/Button/Button';
import { PersonalViewsMutationWrapper } from './PersonalViewsMutationWrapper';
import PersonalViewCopyKeywordButton from './PersonalViewCopyKeywordButton';
import { appendContexteToUrl } from '../../../utils/url';

/**
 * Filter Personal Views component
 * @param {object} params
 * @param {string} params.mutationKey - The mutation key to use for the optimistic update
 * @param {string} params.path
 * @param {import('../../../domain/tablePersonalView/useTablePersonalView').useTablePersonalViewReturn} params.personalViews
 * @param {string} params.personalViewsPath
 * @param {string} params.personalViewsFormUrl
 * @param {(optionId: string) => void} params.handlePersonalViewsSelectedOptionMutation
 * @param {(formUrl: string) => void} params.handleOpenModal
 * @param {ApiManagerPersonalViews} params.managerPersonalViews
 * @param {string | null} [params.contexte] - Page contexte JSON for set-attribut
 * @param {() => void} [params.onResetView] - Called when user clicks "Réinitialiser la vue"
 * @param {boolean} [params.isResetting] - True while reset is in progress
 * @returns {React.ReactNode}
 */
function FilterPersonalViews({
  mutationKey,
  path,
  personalViews,
  personalViewsPath,
  personalViewsFormUrl,
  handlePersonalViewsSelectedOptionMutation,
  handleOpenModal,
  managerPersonalViews,
  contexte = null,
  onResetView,
  isResetting = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    handleSearchPersonalViewsGroups,
    searchPersonalViewsGroups,
    personalViewsListFiltered,
    selectedPersonalViewItem,
    defaultPersonalViewItem,
  } = personalViews;
  return (
    <CustomVueListContainer>
      <CustomVueSectionTitle>
        {t('table.filters.myPersonalViews')}
        <CustomVueSectionTitleActions>
          {onResetView ? (
            <Button
              color="no-background"
              icon="refresh"
              size="small"
              iconPosition="right"
              onClick={onResetView}
              disabled={isResetting || !path}
            >
              {t('table.filters.resetView')}
            </Button>
          ) : null}
          <IconButton
            icon="setting-2"
            color="transparent"
            onClick={() => navigate(personalViewsPath)}
            disabled={!personalViewsPath}
          />
        </CustomVueSectionTitleActions>
      </CustomVueSectionTitle>
      <CustomVueSectionContent>
        <SearchField
          id="table-columns-groups-search"
          placeholder={t('table.filters.columnsGroupsSearch')}
          onFocus={() => {}}
          onChange={(e) => handleSearchPersonalViewsGroups(e.target.value)}
          value={searchPersonalViewsGroups}
        />
      </CustomVueSectionContent>
      <CustomVueList>
        <ColumnsVisibility
          options={personalViewsListFiltered}
          messageNoOptions={t('table.filters.noResult')}
        >
          {(label, options) => (
            <SearchGroupList
              key={label}
              hasLabel={!!label}
              items={options}
              label={label}
            >
              {(option) => {
                const isDefaultView = defaultPersonalViewItem?.id === option.id;
                return (
                  <SearchListItem
                    key={option.id}
                    id={option.id}
                    theme="group"
                    onClick={() => handlePersonalViewsSelectedOptionMutation(option.id)}
                    isSelected={selectedPersonalViewItem?.id === option.id}
                    color={option.color}
                  >
                    {option.lib}
                    <SearchListItemActions>
                      <PersonalViewsMutationWrapper
                        mutationKey={mutationKey}
                        url={appendContexteToUrl(option.setDefaultAttributeUrl, contexte)}
                        path={path}
                        requestBody={{
                          value: isDefaultView ? 'non' : 'oui',
                          isFirstLoopOccurence: true,
                          isLastLoopOccurence: true,
                        }}
                        optimisticPersonalViewsPatch={{
                          defaultOption: isDefaultView ? '' : option.id,
                        }}
                      >
                        {({ mutate, isPending }) => (
                          <IconButton
                            title={isDefaultView
                              ? t('personalViews.removeFromFavorites')
                              : t('personalViews.addToFavorites')}
                            color="transparent"
                            icon={isDefaultView ? 'star-filled' : 'star'}
                            iconColor={isDefaultView ? 'yellow-500' : ''}
                            size="medium"
                            disabled={isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              mutate();
                            }}
                          />
                        )}
                      </PersonalViewsMutationWrapper>
                      <IconButton
                        title={t('personalViews.edit')}
                        icon="edit-2"
                        color="transparent"
                        size="medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(option.formUrl);
                        }}
                      />
                      <PersonalViewsMutationWrapper
                        mutationKey={mutationKey}
                        url={option.deleteBasicUrl}
                        path={path}
                        optimisticPersonalViewsPatch={{
                          options: managerPersonalViews?.options?.filter(
                            (item) => item.id !== option.id,
                          ),
                          defaultOption: option.id === managerPersonalViews?.defaultOption
                            ? ''
                            : managerPersonalViews?.defaultOption,
                        }}
                      >
                        {({ mutate, isPending }) => (
                          <IconButton
                            title={t('personalViews.delete')}
                            icon="trash-empty"
                            color="transparent"
                            size="medium"
                            disabled={isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              // eslint-disable-next-line no-alert
                              const confirmed = window.confirm(t('personalView.confirmDelete'));
                              if (confirmed) {
                                mutate();
                              }
                            }}
                          />
                        )}
                      </PersonalViewsMutationWrapper>
                      {option.keyword ? <PersonalViewCopyKeywordButton keyword={option.keyword} /> : null}
                    </SearchListItemActions>
                  </SearchListItem>
                );
              }}
            </SearchGroupList>
          )}
        </ColumnsVisibility>
      </CustomVueList>
      <CustomVueSectionFooter>
        <Button
          color="no-background"
          icon="add"
          size="small"
          iconPosition="right"
          onClick={() => handleOpenModal(personalViewsFormUrl)}
        >
          {t('table.filters.addCustomView')}
        </Button>
      </CustomVueSectionFooter>
    </CustomVueListContainer>
  );
}

export default FilterPersonalViews;
